import { ColumnDef } from '@tanstack/react-table';
import { CustomTable } from 'components/Table/CustomTable';
import { DateTimeCell } from 'components/Table/Cell/DateTimeCell';
import { useGetCustomReactTable } from 'components/Table/useGetTableSet';
import { TableQueryState } from 'components/Table/useTableQueryState';
import { useMemo, useState } from 'react';
import { ActionCell } from 'components/Table/Cell/ActionCell';
import {
  EEntityState,
  EPermission,
  SmartTextResponse,
  SmartTextResponsePagedData,
} from 'api/core';
import { DeleteEntityById } from 'components/Form/DeleteEntityById';
import { AnimatedIcon } from 'components/Icon/AnimatedIcon';
import { PermissionProtectedComponent } from 'auth/PermissionProtectedComponent';
import { SmartTextCreate } from './SmartTextCreate';
import { useDeleteSmartText, useGetSmartText } from 'api/useSmartTextApi';
import { LinkCell } from 'components/Table/Cell/LinkCell';

interface TableProps {
  data?: SmartTextResponsePagedData;
  isFetching: boolean;
  queryState: TableQueryState;
}

export const SmartTextsTable = ({
  data,
  isFetching,
  queryState,
}: TableProps) => {
  const [deleteTarget, setDeleteTarget] = useState<SmartTextResponse | null>(
    null
  );

  const columns = useMemo(() => {
    const baseColumns: ColumnDef<SmartTextResponse>[] = [
      {
        accessorKey: '_view',
        header: 'Vis',
        enableSorting: false,
        cell: (e) => (
          <LinkCell
            path={`/smart-texts/${e.row.original.id}`}
            tooltip="Vis tekst"
          />
        ),
      },
      {
        accessorKey: '_case.address',
        header: 'Adresse',
      },
      {
        accessorKey: 'user.name',
        header: 'Bruger',
        enableSorting: false,
      },
      {
        accessorKey: 'createdUtc',
        header: 'Oprettet',
        cell: (e) => <DateTimeCell value={e.row.original.createdUtc} />,
      },
      {
        accessorKey: '_',
        header: 'Handlinger',
        enableSorting: false,
        cell: (e) => (
          <div className="flex space-x-5 md:space-x-3">
            <PermissionProtectedComponent
              permissions={[EPermission.SmartTextWrite]}
            >
              <SmartTextCreate
                copyOfId={e.row.original.id}
                trigger={
                  <ActionCell
                    icon={<AnimatedIcon icon="copy-icon" className="h-6 w-6" />}
                    onClick={() => {
                      // Do nothing, handled by SmartTextCreate
                    }}
                    tooltip="Lav kopi af denne tekst"
                  />
                }
              />
            </PermissionProtectedComponent>

            {e.row.original.entityState != EEntityState.Archived ? (
              <ActionCell
                permissions={[EPermission.SmartTextDelete]}
                icon={
                  <AnimatedIcon
                    icon="trash-icon"
                    className="h-8 md:h-6 w-8 md:w-6"
                  />
                }
                onClick={() => setDeleteTarget(e.row.original)}
                tooltip="Arkiver tekst"
              />
            ) : null}
          </div>
        ),
      },
    ];
    return baseColumns;
  }, [setDeleteTarget]);

  const table = useGetCustomReactTable(
    data?.data ?? [],
    data?.page.totalPages ?? -1,
    columns,
    queryState
  );

  return (
    <>
      <CustomTable table={table} isLoading={isFetching} showToggleArchived />

      {deleteTarget ? (
        <DeleteEntityById
          id={deleteTarget?.id}
          isInitialOpen={true}
          onClosed={() => setDeleteTarget(null)}
          title={deleteTarget._case.address}
          getter={useGetSmartText}
          deleter={useDeleteSmartText}
          titleFn={(entityTitle) => `Arkiver ${entityTitle}?`}
          descriptionFn={(entityTitle) =>
            `Er du sikker på du vil arkivere '${entityTitle}'? Dette kan ikke fortrydes, men du kan finde den under arkiverede tekster.`
          }
        />
      ) : null}
    </>
  );
};
